import type { MentionContextProps } from '@halo/ui-chat-react';
import { EditorContext } from '@halo/ui-chat-react';
import type { GroupMember } from '@halo/chat-sdk';
import { useActionsStore } from '@/store/actions';

export interface SessionEditorContextProps {
  children: ReactNode;
  disabledMentionAll?: boolean;
  getMembers?: (search: string) => GroupMember[] | Promise<GroupMember[]>;
  onMentionOpenChange?: (open: boolean) => void;
}

export function SessionEditorContext({
  children,
  disabledMentionAll,
  getMembers,
  onMentionOpenChange,
}: SessionEditorContextProps) {
  const disabledMention = useMemo(() => {
    return !getMembers;
  }, [getMembers]);

  const filterUsers = useCallback(
    async (search: string) => {
      const members = await getMembers?.(search);

      if (!members) return [];

      return members.filter((member) => {
        const reg = new RegExp(search, 'i');
        return reg.test(member.name) || reg.test(member.username);
      });
    },
    [getMembers]
  );

  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);

  const mention = useMemo<MentionContextProps>(() => {
    return {
      portal: {
        filterUsers,
        disabled: disabledMention,
        canAtAll: !disabledMentionAll,
        onOpenChange: onMentionOpenChange,
      },
      element: {
        onClick: (user) => {
          fetchProfileUser(user.user_id);
        },
      },
    };
  }, [filterUsers, disabledMention, disabledMentionAll, fetchProfileUser, onMentionOpenChange]);

  return <EditorContext mention={mention}>{children}</EditorContext>;
}
